import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/api";

const PAGE = 60;

// Loads a category/pack and its stickers 60 at a time
export function usePagedDetail(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);

  useEffect(() => {
    setLoading(true);
    setData(null);
    api.get(url, { params: { skip: 0, limit: PAGE } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [url]);

  const loadMore = useCallback(async () => {
    if (!data || more || data.stickers.length >= data.sticker_count) return;
    setMore(true);
    try {
      const r = await api.get(url, { params: { skip: data.stickers.length, limit: PAGE } });
      setData((d) => ({ ...d, stickers: [...d.stickers, ...r.data.stickers.filter((s) => !d.stickers.some((x) => x.id === s.id))] }));
    } finally { setMore(false); }
  }, [url, data, more]);

  const hasMore = !!data && data.stickers.length < data.sticker_count;
  return { data, loading, hasMore, loadingMore: more, loadMore };
}

// Loads the next page automatically when the end of the list comes into view
export function LoadMore({ hasMore, loading, onMore }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!hasMore || !ref.current) return;
    const io = new IntersectionObserver((e) => e[0].isIntersecting && onMore(), { rootMargin: "2400px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [hasMore, onMore]);
  if (!hasMore) return null;
  return (
    <div ref={ref} className="flex justify-center py-6">
      <button onClick={onMore} disabled={loading} data-testid="load-more-stickers" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white border border-[#C58B90] text-[#C58B90] text-sm font-semibold hover:bg-[#F7EBEC] transition-colors disabled:opacity-70">
        {loading && <Loader2 size={16} className="animate-spin" />} Carregar mais
      </button>
    </div>
  );
}
